// Spec 083 — o PERÍODO do relatório, em datas civis.
//
// Tudo aqui opera sobre `YYYY-MM-DD` e NUNCA sobre instantes. O fuso entra em um único lugar — a
// pergunta "que dia é hoje para esta organização" — e sai logo em seguida. Depois disso a
// aritmética é civil e determinística, sem horário de verão, sem offset e sem a hora de parede da
// máquina que roda o processo.
//
// Por que isso importa: todo o relatório parte de um par de datas. Se "hoje" escorregar um dia, o
// mês corrente começa no dia errado e TODOS os agrupamentos andam junto — sem nada falhar, sem nada
// logar, com o total ainda fechando com a grade.
//
// Os tetos (`REPORT_MAX_RANGE_DAYS`, `REPORT_DAILY_GROUPING_MAX_DAYS`) moram no contrato, ao lado do
// schema que os cobra. Aqui ficam só as funções puras que a tela e a rota compartilham.
import {
  REPORT_DAILY_GROUPING_MAX_DAYS,
  REPORT_MAX_RANGE_DAYS,
  type DateOnly,
  type ReportGroupBy,
  type ReportPreset,
} from "../api-contracts/production-report.js";

const MS_PER_DAY = 86_400_000;

/** `YYYY-MM-DD` → epoch da meia-noite UTC daquela data. Só para aritmética; nunca exibido. */
function toUtcMidnight(date: DateOnly): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

function fromUtcMidnight(ms: number): DateOnly {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Soma (ou subtrai) dias de uma data civil. Sem fuso: 28/02 + 1 é 01/03 em qualquer lugar. */
export function addDays(date: DateOnly, days: number): DateOnly {
  return fromUtcMidnight(toUtcMidnight(date) + days * MS_PER_DAY);
}

/**
 * Número de dias do intervalo, INCLUSIVO nas duas pontas. Um dia sozinho conta 1 — é o que o gestor
 * entende por "o período de hoje até hoje".
 */
export function rangeDays(from: DateOnly, to: DateOnly): number {
  return Math.round((toUtcMidnight(to) - toUtcMidnight(from)) / MS_PER_DAY) + 1;
}

export function exceedsMaxRange(from: DateOnly, to: DateOnly): boolean {
  return rangeDays(from, to) > REPORT_MAX_RANGE_DAYS;
}

export function isDailyGroupingAllowed(from: DateOnly, to: DateOnly): boolean {
  return rangeDays(from, to) <= REPORT_DAILY_GROUPING_MAX_DAYS;
}

/**
 * A janela anterior de MESMO tamanho, terminando na véspera do início. É o que alimenta a variação
 * em pontos percentuais (FR-019) — comparar contra "o mês passado inteiro" quando o período tem
 * dezoito dias compararia conjuntos de tamanhos diferentes.
 */
export function previousPeriod(from: DateOnly, to: DateOnly): { from: DateOnly; to: DateOnly } {
  const length = rangeDays(from, to);
  const prevTo = addDays(from, -1);
  return { from: addDays(prevTo, -(length - 1)), to: prevTo };
}

/**
 * Que dia é hoje PARA ESTA ORGANIZAÇÃO. Único ponto do módulo em que o fuso aparece.
 *
 * `formatToParts` em vez de `toLocaleDateString` com uma locale que "costuma" dar ISO: a locale é
 * ambiente, e ambiente muda entre a máquina do dev, o contêiner e o teste.
 */
export function todayInTimeZone(timeZone: string, now: Date = new Date()): DateOnly {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Primeiro dia civil do mês de `date`. */
function firstOfMonth(date: DateOnly): DateOnly {
  return `${date.slice(0, 7)}-01`;
}

/**
 * Resolve um preset em par de datas civis no fuso da organização.
 *
 * `thisMonth` e `thisYear` terminam em HOJE, não no fim do mês nem do ano: consultar o futuro
 * devolveria agrupamentos vazios e um total que ninguém pediu. O agrupamento corrente ainda aparece
 * marcado como parcial, que é a informação que o gestor precisa.
 */
export function resolveReportPeriod(
  preset: ReportPreset,
  timeZone: string,
  custom?: { from: DateOnly; to: DateOnly },
  now: Date = new Date()
): { from: DateOnly; to: DateOnly } {
  if (preset === "custom") {
    if (!custom) {
      throw new Error("Período personalizado exige as datas de início e fim.");
    }
    return { from: custom.from, to: custom.to };
  }

  const today = todayInTimeZone(timeZone, now);

  switch (preset) {
    case "7d":
      return { from: addDays(today, -6), to: today };
    case "30d":
      return { from: addDays(today, -29), to: today };
    case "thisMonth":
      return { from: firstOfMonth(today), to: today };
    case "lastMonth": {
      const lastDayOfPreviousMonth = addDays(firstOfMonth(today), -1);
      return { from: firstOfMonth(lastDayOfPreviousMonth), to: lastDayOfPreviousMonth };
    }
    case "thisYear":
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
  }
}

// --- Baldes do agrupamento ---------------------------------------------------
//
// A série de baldes é gerada a partir do PERÍODO, e não do que o banco devolveu: um agrupamento sem
// nenhum registro precisa aparecer vazio, não sumir. Sumir faria a grade mentir por omissão — a
// semana de parada apareceria como se não existisse, e a média mensal seria calculada sobre menos
// semanas do que o mês tem.

/** O dia da semana, 0 = segunda. `getUTCDay` devolve 0 = domingo. */
function mondayIndex(date: DateOnly): number {
  return (new Date(`${date}T00:00:00.000Z`).getUTCDay() + 6) % 7;
}

/** O rótulo do balde que CONTÉM esta data. Semana começa na segunda; mês é civil. */
export function bucketKeyFor(date: DateOnly, groupBy: ReportGroupBy): DateOnly {
  if (groupBy === "day") return date;
  if (groupBy === "week") return addDays(date, -mondayIndex(date));
  return `${date.slice(0, 7)}-01`;
}

/** A faixa NATURAL do balde, sem recorte pelo período. É ela que decide se o balde é parcial. */
export function bucketNaturalRange(
  key: DateOnly,
  groupBy: ReportGroupBy
): { from: DateOnly; to: DateOnly } {
  if (groupBy === "day") return { from: key, to: key };
  if (groupBy === "week") return { from: key, to: addDays(key, 6) };
  const [y, m] = key.split("-").map(Number) as [number, number];
  const firstOfNext = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
  return { from: key, to: addDays(firstOfNext, -1) };
}

export interface ReportBucketRange {
  key: DateOnly;
  /** Faixa já RECORTADA pelo período — é o que a grade rotula. */
  from: DateOnly;
  to: DateOnly;
  /**
   * A faixa natural do balde passa do período ou de hoje, ou começa antes do período.
   *
   * As três condições são a mesma coisa dita de três jeitos: o balde mostra menos do que o rótulo
   * dele promete. Uma semana com três dias de dado rotulada como semana inteira é a mesma mentira
   * que um mês corrente rotulado como mês fechado — que é justamente o que a planilha da fábrica
   * marca como parcial.
   */
  partial: boolean;
}

/** Todos os baldes que tocam o período, em ordem, inclusive os sem nenhum registro. */
export function bucketSeries(
  from: DateOnly,
  to: DateOnly,
  groupBy: ReportGroupBy,
  today: DateOnly
): ReportBucketRange[] {
  const out: ReportBucketRange[] = [];
  let key = bucketKeyFor(from, groupBy);
  while (key <= to) {
    const natural = bucketNaturalRange(key, groupBy);
    out.push({
      key,
      from: natural.from < from ? from : natural.from,
      to: natural.to > to ? to : natural.to,
      partial: natural.from < from || natural.to > to || natural.to > today,
    });
    key = bucketKeyFor(addDays(natural.to, 1), groupBy);
  }
  return out;
}
